/**
 * Where a tapped notification should open — the ONE decision, shared by the
 * service worker (which inlines this function's source) and the unit tests.
 *
 * Why it exists: the white-label app lives at `/{slug}/` (ADR-0039), but push
 * payloads have historically carried ROOT-relative links (`/queue/abc`). Opened
 * as-is, that lands on the marketing origin root instead of the operator's app.
 * The server now sends slug-aware links; this is the defensive second line so a
 * stale or hand-written link still lands inside the right tenant.
 *
 * Rules, in order:
 *   1. No link, a malformed link, or a link to ANOTHER origin → the app's home
 *      (`origin + base`). A notification must never navigate off the origin.
 *   2. `base` is `/` (dashboard, agent app, bare-host dev) → the link unchanged.
 *   3. The path is already under `base` → unchanged.
 *   4. The path's first segment is one of the app's own route roots (or the
 *      path is `/`) → it is root-relative, so prefix `base`.
 *   5. Anything else names its own first segment — another tenant's explicit
 *      link (`/other-shop/queue/1`) — so it is left alone.
 *
 * SELF-CONTAINED ON PURPOSE: the service worker template embeds this function
 * via `Function.prototype.toString`, so it may not reference imports, module
 * constants, or declare nested named functions (a bundler's keep-names helper
 * would not exist inside the worker). The unit test rehydrates it from its own
 * source text to prove that stays true.
 */
export function resolveNotificationUrl(
  link: string | null | undefined,
  base: string,
  origin: string,
  routeRoots: readonly string[] | null,
): string {
  const safeBase = typeof base === 'string' && /^\/(?:[a-z0-9-]+\/)?$/.test(base) ? base : '/';
  const home = origin + safeBase;
  if (typeof link !== 'string' || link.trim() === '') return home;

  let url: URL;
  try {
    url = new URL(link, home);
  } catch {
    return home;
  }
  if (url.origin !== origin) return home;
  if (safeBase === '/') return url.href;

  const path = url.pathname;
  if (path.startsWith(safeBase) || path === safeBase.slice(0, -1)) return url.href;

  const first = path.split('/').filter(Boolean)[0] ?? '';
  const isAppRoute = first === '' || routeRoots === null || routeRoots.indexOf(first) !== -1;
  if (!isAppRoute) return url.href;

  return origin + safeBase + path.slice(1) + url.search + url.hash;
}

/**
 * Turn an absolute in-app URL into the path a `<BrowserRouter basename>` wants.
 * Returns null when the URL is off-origin or outside the router's basename, so
 * the caller falls back to a full navigation instead of a wrong in-app route.
 */
export function toRouterPath(href: string, origin: string, basename: string): string | null {
  let url: URL;
  try {
    url = new URL(href, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin) return null;
  const base = basename.endsWith('/') ? basename : `${basename}/`;
  const tail = url.search + url.hash;
  if (base === '/') return url.pathname + tail;
  if (url.pathname === base.slice(0, -1)) return `/${tail}`;
  if (!url.pathname.startsWith(base)) return null;
  return `/${url.pathname.slice(base.length)}${tail}`;
}

/**
 * A push deep link → the router path to navigate to, or null when it resolves
 * outside this router (the caller then does a full-page navigation to `href`).
 */
export function pushLinkTarget(
  link: string | null | undefined,
  opts: {
    readonly base: string;
    readonly origin: string;
    readonly routeRoots: readonly string[] | null;
  },
): { readonly href: string; readonly routerPath: string | null } {
  const href = resolveNotificationUrl(link, opts.base, opts.origin, opts.routeRoots);
  return { href, routerPath: toRouterPath(href, opts.origin, opts.base) };
}
